import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Users } from 'lucide-react';

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <div className="container py-5">
        {/* Back Button */}
        <Link
          to="/"
          className="btn btn-link text-primary text-decoration-none mb-4 p-0"
        >
          <ArrowLeft className="me-2" size={16} />
          Back to Home
        </Link>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card border-0 shadow-lg">
              <div className="card-header bg-success text-white p-4">
                <div className="d-flex align-items-center gap-3">
                  <Shield size={32} />
                  <div>
                    <h1 className="h2 mb-1">Privacy Policy</h1>
                    <p className="mb-0 opacity-90">Last updated: JULY 2025</p>
                  </div>
                </div>
              </div>

              <div className="card-body p-5">
                <div className="alert alert-success d-flex align-items-start gap-3">
                  <Lock size={20} className="mt-1" />
                  <div>
                    <strong>Your Privacy Matters:</strong> We are committed to protecting your personal information and being transparent about how we collect, use, and share your data.
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">1. Information We Collect</h2>
                  
                  <h3 className="h5 fw-semibold mb-3 d-flex align-items-center gap-2">
                    <Users size={18} />
                    Personal Information
                  </h3>
                  <div className="text-muted mb-4">
                    <p className="mb-3">We collect information you provide directly to us, such as:</p>
                    <ul>
                      <li>Name and contact information (email, phone number)</li>
                      <li>Academic information (exam scores, ranks, preferences)</li>
                      <li>Account credentials and profile information</li>
                      <li>Payment information (processed securely through Razorpay)</li>
                      <li>Communication preferences</li>
                    </ul>
                  </div>

                  <h3 className="h5 fw-semibold mb-3 d-flex align-items-center gap-2">
                    <Eye size={18} />
                    Usage Information
                  </h3>
                  <div className="text-muted">
                    <p className="mb-3">We automatically collect certain information when you use our services:</p>
                    <ul>
                      <li>Device information (IP address, browser type, operating system)</li>
                      <li>Usage patterns and preferences</li>
                      <li>Pages visited and time spent on our website</li>
                      <li>Search queries and interactions with our content</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">2. How We Use Your Information</h2>
                  <div className="text-muted">
                    <p className="mb-3">We use the information we collect to:</p>
                    <ul>
                      <li>Provide personalized college recommendations</li>
                      <li>Process payments and deliver purchased services</li>
                      <li>Send important updates and notifications</li>
                      <li>Improve our services and user experience</li>
                      <li>Respond to your inquiries and provide customer support</li>
                      <li>Conduct research and analytics to enhance our platform</li>
                      <li>Comply with legal obligations</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3 d-flex align-items-center gap-2">
                    <Database size={20} />
                    Information Sharing and Disclosure
                  </h2>
                  <div className="text-muted">
                    <p className="mb-3">We do not sell, trade, or rent your personal information. We may share your information only in the following circumstances:</p>
                    <ul>
                      <li><strong>With your consent:</strong> When you explicitly agree to share information</li>
                      <li><strong>Service providers:</strong> With trusted third-party services that help us operate our platform (e.g., payment processors, email services)</li>
                      <li><strong>Legal requirements:</strong> When required by law or to protect our rights and safety</li>
                      <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">3. Data Security</h2>
                  <div className="text-muted">
                    <p className="mb-3">We implement appropriate security measures to protect your information:</p>
                    <ul>
                      <li>Encryption of sensitive data in transit and at rest</li>
                      <li>Secure payment processing through Razorpay</li>
                      <li>Regular security audits and updates</li>
                      <li>Access controls and authentication measures</li>
                      <li>Employee training on data protection practices</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">4. Cookies and Tracking Technologies</h2>
                  <div className="text-muted">
                    <p className="mb-3">We use cookies and similar technologies to:</p>
                    <ul>
                      <li>Remember your preferences and settings</li>
                      <li>Analyze website traffic and usage patterns</li>
                      <li>Provide personalized content and recommendations</li>
                      <li>Improve our services and user experience</li>
                    </ul>
                    <p className="mt-3">You can control cookie settings through your browser preferences.</p>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">5. Your Rights and Choices</h2>
                  <div className="text-muted">
                    <p className="mb-3">You have the following rights regarding your personal information:</p>
                    <ul>
                      <li><strong>Access:</strong> Request a copy of your personal information</li>
                      <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                      <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                      <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                      <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                    </ul>
                    <p className="mt-3">To exercise these rights, please contact us at hello@gradvikalp.com</p>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">6. Data Retention</h2>
                  <p className="text-muted">
                    We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. When we no longer need your information, we will securely delete or anonymize it.
                  </p>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">7. Children's Privacy</h2>
                  <p className="text-muted">
                    Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
                  </p>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">8. International Data Transfers</h2>
                  <p className="text-muted">
                    Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.
                  </p>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">9. Changes to This Privacy Policy</h2>
                  <p className="text-muted">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
                  </p>
                </div>

                <div className="mb-5">
                  <h2 className="h4 fw-bold text-success mb-3">10. Contact Us</h2>
                  <div className="text-muted">
                    <p className="mb-3">If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
                    <ul className="list-unstyled">
                      <li><strong>Email:</strong> vikalpgrad@gmail.com</li>
                      <li><strong>Phone:</strong> +91 7568377278</li>
                      <li><strong>Address:</strong> Jaipur, India</li>
                    </ul>
                  </div>
                </div>

                <div className="alert alert-info">
                  <strong>Transparency Commitment:</strong> We believe in being transparent about our data practices. If you have any concerns or questions about how we handle your information, please don't hesitate to reach out to us.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;